import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  Bell, Briefcase, CalendarDays, FileText, LayoutDashboard, LogOut, Menu,
  Newspaper, Scale, Settings, Users, X, UserCircle2, ChevronRight,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { timeAgo } from "@/lib/format";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const NAV = [
  { to: "/dashboard", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/dashboard/clients", label: "Clients", icon: Users },
  { to: "/dashboard/cases", label: "Cases", icon: Briefcase },
  { to: "/dashboard/documents", label: "Documents", icon: FileText },
  { to: "/dashboard/appointments", label: "Appointments", icon: CalendarDays },
  { to: "/dashboard/blog", label: "Blog", icon: Newspaper },
  { to: "/dashboard/profile", label: "Profile", icon: UserCircle2 },
  { to: "/dashboard/notifications", label: "Notifications", icon: Bell },
  { to: "/dashboard/settings", label: "Settings", icon: Settings },
];

const TITLES = {
  "/dashboard": "Dashboard Overview",
  "/dashboard/clients": "Clients",
  "/dashboard/cases": "Cases",
  "/dashboard/documents": "Documents",
  "/dashboard/appointments": "Appointments & Calendar",
  "/dashboard/blog": "Blog Management",
  "/dashboard/profile": "Attorney Profile",
  "/dashboard/notifications": "Notifications",
  "/dashboard/settings": "Settings",
};

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [notifs, setNotifs] = useState([]);

  useEffect(() => { setSidebarOpen(false); }, [location.pathname]);

  useEffect(() => {
    const load = () => api.get("/notifications")
      .then(({ data }) => { setUnread(data.unread); setNotifs(data.items.slice(0, 5)); })
      .catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [location.pathname]);

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  const crumbs = location.pathname.split("/").filter(Boolean);
  const title = TITLES[location.pathname] || (crumbs.includes("clients") && crumbs.length > 2 ? "Client Detail" : TITLES[`/${crumbs.slice(0, 2).join("/")}`] || "Dashboard");

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      <Link to="/" className="flex items-center gap-3 px-6 h-20 border-b border-ivory/10" data-testid="sidebar-logo">
        <span className="h-9 w-9 bg-gold flex items-center justify-center shrink-0">
          <Scale className="h-4 w-4 text-navy" />
        </span>
        <div>
          <p className="font-serif font-semibold text-ivory leading-tight">Wanar & Associates</p>
          <p className="text-[10px] uppercase tracking-widest text-ivory/40">Attorney Workspace</p>
        </div>
      </Link>
      <nav className="flex-1 py-6 px-3 space-y-1 overflow-y-auto" data-testid="sidebar-nav">
        {NAV.map(({ to, label, icon: Icon, end }) => (
          <NavLink key={to} to={to} end={end} data-testid={`sidebar-${label.toLowerCase()}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors duration-200 border-l-2 ${isActive
                ? "border-gold text-gold bg-ivory/5"
                : "border-transparent text-ivory/60 hover:text-ivory hover:bg-ivory/5"}`}>
            <Icon className="h-4.5 w-4.5 h-[18px] w-[18px]" /> {label}
          </NavLink>
        ))}
      </nav>
      <div className="p-4 border-t border-ivory/10">
        <Link to="/design-system" data-testid="sidebar-design-system"
          className="flex items-center justify-between px-4 py-3 text-xs font-semibold uppercase tracking-wider text-ivory/50 hover:text-gold transition-colors">
          Design System <ChevronRight className="h-3.5 w-3.5" />
        </Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background flex" data-testid="dashboard-layout">
      <aside className="hidden lg:block w-[260px] shrink-0 bg-navy fixed inset-y-0 left-0 z-40" data-testid="dashboard-sidebar">
        <SidebarContent />
      </aside>

      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50" data-testid="mobile-sidebar-overlay">
          <div className="absolute inset-0 bg-navy/60" onClick={() => setSidebarOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-[280px] bg-navy shadow-2xl">
            <button className="absolute top-5 right-4 text-ivory/70 hover:text-ivory" onClick={() => setSidebarOpen(false)} data-testid="mobile-sidebar-close">
              <X className="h-5 w-5" />
            </button>
            <SidebarContent />
          </aside>
        </div>
      )}

      <div className="flex-1 lg:ml-[260px] flex flex-col min-w-0">
        <header className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-border h-16 flex items-center gap-4 px-4 sm:px-6" data-testid="dashboard-topbar">
          <button className="lg:hidden p-2 text-navy" onClick={() => setSidebarOpen(true)} data-testid="topbar-menu-btn" aria-label="Open menu">
            <Menu className="h-5 w-5" />
          </button>
          <nav className="hidden sm:flex items-center gap-2 text-sm" data-testid="breadcrumbs">
            <Link to="/dashboard" className="text-muted-foreground hover:text-navy">Dashboard</Link>
            {crumbs.slice(1).map((c, i) => (
              <span key={i} className="flex items-center gap-2">
                <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/60" />
                <span className="text-navy font-medium capitalize">{c}</span>
              </span>
            ))}
          </nav>
          <h1 className="sm:hidden font-serif text-lg font-semibold text-navy">{title}</h1>
          <div className="flex-1" />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="relative p-2 text-navy hover:bg-muted transition-colors" data-testid="notifications-bell" aria-label="Notifications">
                <Bell className="h-5 w-5" />
                {unread > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 h-5 min-w-5 px-1 bg-gold text-navy text-[10px] font-bold flex items-center justify-center rounded-full" data-testid="notifications-badge">
                    {unread}
                  </span>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80">
              <div className="px-3 py-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground">Recent Notifications</div>
              {notifs.length === 0 && <div className="px-3 py-4 text-sm text-muted-foreground">No notifications.</div>}
              {notifs.map((n) => (
                <DropdownMenuItem key={n.id} className="flex flex-col items-start gap-0.5 px-3 py-2.5 cursor-pointer"
                  onClick={() => navigate(n.link || "/dashboard/notifications")}>
                  <span className={`text-sm ${n.read ? "text-muted-foreground" : "font-semibold text-navy"}`}>{n.title}</span>
                  <span className="text-xs text-muted-foreground">{timeAgo(n.created_at)}</span>
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem className="justify-center text-xs font-semibold uppercase tracking-wider text-gold-700 cursor-pointer"
                onClick={() => navigate("/dashboard/notifications")} data-testid="view-all-notifications">
                View all
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-3 pl-2 pr-3 py-1.5 hover:bg-muted transition-colors" data-testid="user-menu-btn">
                <span className="h-9 w-9 bg-navy text-gold font-serif font-semibold flex items-center justify-center">
                  {(user?.name || "A").split(" ").map((w) => w[0]).slice(0, 2).join("")}
                </span>
                <span className="hidden md:block text-left">
                  <span className="block text-sm font-semibold text-navy leading-tight">{user?.name}</span>
                  <span className="block text-[11px] text-muted-foreground">Principal Attorney</span>
                </span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuItem onClick={() => navigate("/dashboard/profile")} className="cursor-pointer" data-testid="menu-profile">
                <UserCircle2 className="h-4 w-4 mr-2" /> Profile
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/dashboard/settings")} className="cursor-pointer" data-testid="menu-settings">
                <Settings className="h-4 w-4 mr-2" /> Settings
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={doLogout} className="cursor-pointer text-red-600 focus:text-red-600" data-testid="menu-logout">
                <LogOut className="h-4 w-4 mr-2" /> Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-[1400px] w-full mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
